import {
  ConsentService,
  type ConsentCategories,
  type ConsentState,
} from "../services/ConsentService";

export class ConsentBanner {
  private readonly element: HTMLElement;
  private readonly advertising: HTMLInputElement;
  private readonly analytics: HTMLInputElement;

  constructor(
    private readonly service: ConsentService,
    mount: HTMLElement = document.body,
  ) {
    this.element = document.createElement("div");
    this.element.className = "consent-banner";
    this.element.setAttribute("aria-label", "Privacy choices");
    this.element.setAttribute("role", "dialog");
    this.element.setAttribute("aria-modal", "true");
    this.element.innerHTML = `
      <div class="consent-copy">
        <strong>Your privacy choices</strong>
        <p>Essential local storage saves progress and your choice. Optional advertising and analytics stay off unless you enable them. Personalized ads remain disabled.</p>
        <div class="consent-options">
          <label><span><b>Essential</b><small>Save, settings, security and consent record</small></span><input type="checkbox" checked disabled></label>
          <label><span><b>Advertising</b><small>Allow non-personalized ad storage after a certified CMP is configured</small></span><input type="checkbox" data-consent-category="advertising"></label>
          <label><span><b>Analytics</b><small>Allow privacy-safe usage measurement when analytics is configured</small></span><input type="checkbox" data-consent-category="analytics"></label>
        </div>
        <nav>
          <a href="./privacy.html">Privacy</a>
          <a href="./cookies.html">Cookies and storage</a>
        </nav>
      </div>
      <div class="consent-actions">
        <button class="button secondary" type="button" data-consent="close">Close</button>
        <button class="button secondary" type="button" data-consent="reject">Reject optional</button>
        <button class="button secondary" type="button" data-consent="save">Save choices</button>
        <button class="button" type="button" data-consent="accept">Accept optional</button>
      </div>`;
    mount.append(this.element);
    this.advertising = this.requireInput("advertising");
    this.analytics = this.requireInput("analytics");
    this.element.querySelector("[data-consent='accept']")?.addEventListener("click", () => {
      this.service.acceptOptional();
    });
    this.element.querySelector("[data-consent='reject']")?.addEventListener("click", () => {
      this.service.rejectOptional();
    });
    this.element.querySelector("[data-consent='save']")?.addEventListener("click", () => {
      this.service.savePreferences(this.selectedCategories());
    });
    this.element.querySelector("[data-consent='close']")?.addEventListener("click", () => {
      if (this.service.hasChoice()) this.element.hidden = true;
    });
    // Escape is the keyboard equivalent of the Close button above, gated by
    // the exact same condition - a mandatory first-visit consent choice
    // stays mandatory (no Close button, no Escape dismissal either); once a
    // choice already exists and this is reopened to review/change it (see
    // open()), Escape closes it just like clicking Close does.
    this.element.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.service.hasChoice()) {
        this.element.hidden = true;
      }
    });
    this.service.subscribe((state) => this.render(state));
    this.render(this.service.getState());
  }

  open(): void {
    this.syncInputs(this.service.getState());
    this.element.hidden = false;
    this.element.querySelector<HTMLButtonElement>("[data-consent='reject']")?.focus();
  }

  private render(state: ConsentState): void {
    this.syncInputs(state);
    const close = this.element.querySelector<HTMLButtonElement>("[data-consent='close']");
    if (close) close.hidden = state.choice === "pending";
    this.element.hidden = state.choice !== "pending";
  }

  private syncInputs(state: ConsentState): void {
    this.advertising.checked = state.categories.advertising === "granted";
    this.analytics.checked = state.categories.analytics === "granted";
  }

  private selectedCategories(): ConsentCategories {
    return {
      advertising: this.advertising.checked ? "granted" : "denied",
      analytics: this.analytics.checked ? "granted" : "denied",
    };
  }

  private requireInput(category: keyof ConsentCategories): HTMLInputElement {
    const input = this.element.querySelector<HTMLInputElement>(
      `[data-consent-category="${category}"]`,
    );
    if (!input) throw new Error(`Missing consent input ${category}`);
    return input;
  }
}
